<script lang="ts">
export type AssetSpecValue = string | number | boolean | null;

export type AssetSpecItem = [TranslationKey, AssetSpecValue];

export type AssetSpecsGroup = AssetSpecItem[];

export type AssetSpecsSection = AssetSpecsGroup[];

export type StateOfUseColor = 'success' | 'lime' | 'warning' | 'error' | 'neutral';

export type AssetStateOfUseItem = {
    description: string;
    color: StateOfUseColor;
};

export type SaleStateOfUse = AssetStateOfUseItem | AssetStateOfUseItem[] | undefined;
</script>
<script setup lang="ts">
import type { AccordionItem } from '#ui/types';
import lodashCountBy from 'lodash-es/countBy';
import lodashUniqBy from 'lodash-es/uniqBy';

type Icon = {
    name: string;
    class: string;
};

interface SdpSpecsSectionProps {
    title: string;
    defaultOpen?: boolean;
    groupedByAsset?: boolean;
    specs: AssetSpecsSection;
    stateOfUse?: SaleStateOfUse;
}

// PROPS
const { title, specs, stateOfUse } = defineProps<SdpSpecsSectionProps>();

// COMPOSABLES
const { tt } = useTypedI18n();

// DATA
const isNotEmpty = computed<boolean>(() => !!stateOfUse || specs.some(group => group.some(spec => spec[1] !== null)));
const adaptedStateOfUse = computed(() => {
    if (!stateOfUse) return undefined;

    if (!Array.isArray(stateOfUse)) return [{ ...stateOfUse, icon: getIcon(stateOfUse?.color), count: undefined }];

    const counter = lodashCountBy(stateOfUse, item => item.color);

    return lodashUniqBy(stateOfUse, item => item.color)
        .map(item => ({ ...item, icon: getIcon(item.color), count: counter[item.color] ?? 1 }))
        .toSorted((a, b) => {
            const order = { success: 0, lime: 1, warning: 2, error: 3, neutral: 4 };

            return order[a.color] - order[b.color];
        });
});
const sectionStateOfUse = computed<({ icon?: Icon } & { description: string }) | undefined>(() => {
    if (!stateOfUse) return undefined;

    if (!Array.isArray(stateOfUse)) return { icon: getIcon(stateOfUse.color), description: stateOfUse.description };
});
const accordionItems = computed<AccordionItem[]>(() => [{ label: title }]);

// METHODS
const getIcon = (color?: StateOfUseColor): Icon | undefined => {
    switch (color) {
        case 'success':
            return { name: 'lucide:circle-check', class: 'text-success-500' };
        case 'lime':
            return { name: 'lucide:circle-check', class: 'text-lime-500' };
        case 'warning':
            return { name: 'lucide:circle-alert', class: 'text-warning-500' };
        case 'error':
            return { name: 'lucide:triangle-alert', class: 'text-error-500' };
        case 'neutral':
            return { name: 'lucide:circle-x', class: 'text-neutral-500' };

        default:
            return undefined;
    }
};
</script>

<template>
    <BaseAccordion v-if="isNotEmpty" size="xl" :items="accordionItems" :default-open="defaultOpen">
        <template v-if="sectionStateOfUse?.icon" #trailingIcon>
            <BaseTooltip :text="sectionStateOfUse.description">
                <UIcon class="size-5" :name="sectionStateOfUse.icon.name" :class="sectionStateOfUse.icon.class" />
            </BaseTooltip>
        </template>
        <template #content>
            <div v-if="stateOfUse" class="mb-3 flex flex-col items-stretch gap-2">
                <div v-for="(item, index) in adaptedStateOfUse" :key="index" class="flex flex-1 flex-col items-stretch gap-1">
                    <div v-if="!!item.count" class="flex items-center gap-1">
                        <UIcon v-if="item.icon" class="h-6 w-4" :name="item.icon.name" :class="item.icon.class" />
                        <span>{{ tt('asset.vehicleCount', { count: item.count }) }}</span>
                    </div>
                    <span class="text-dimmed">{{ item.description }}</span>
                </div>
            </div>

            <div class="mb-3 flex flex-col gap-6 md:grid md:grid-cols-2">
                <template v-for="(group, i) in specs" :key="i">
                    <div v-if="group.some(spec => spec[1] !== null)" class="flex flex-col gap-0">
                        <h3 v-if="groupedByAsset" class="text-highlighted text-base leading-6 font-medium">
                            {{ tt('asset.vehicleIndex', { index: i + 1 }) }}
                        </h3>
                        <SdpSpecItem v-for="(spec, j) in group" :key="j" :name="tt(spec[0])" :value="spec[1]" />
                    </div>
                </template>
            </div>
        </template>
    </BaseAccordion>
</template>
